// Same logic as landing and v2-frontend authGuard
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getV2Token } from '../utils/v2AuthUtils';
import SecureCookieManager from '../utils/secureCookie';
import { validateToken } from '../utils/tokenValidator';
import { clearV2Token } from '../utils/v2AuthUtils';
import { isDemoUser } from '../utils/demoAuth';
import { useSubscription } from '../context/SubscriptionContext';
import { useTranslation } from 'react-i18next';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { status: subscriptionStatus, loading: subscriptionLoading } = useSubscription();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = getV2Token();
        
        // Allow access to login, error, and subscription pages (same as landing and v2-frontend)
        if (location.pathname === '/login' || location.pathname.startsWith('/error') || 
            location.pathname === '/subscription' || location.pathname === '/renew-subscription' ||
            location.pathname === '/register') {
          setIsLoading(false);
          return;
        }

        if (!token) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Validate token locally (same as landing and v2-frontend - client-side validation)
        const userData = validateToken(token);
        if (!userData) {
          // Token invalid or expired, clear it (same as landing and v2-frontend)
          const domain = process.env.REACT_APP_DOMAIN || window.location.hostname;
          clearV2Token(domain);
          SecureCookieManager.clearAuthCookies();
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Token is valid, store it in front_compliance's token cookie for backward compatibility
        SecureCookieManager.setAuthToken(token);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error checking authentication:', error);
        // On error, clear token and deny access (same as landing and v2-frontend)
        const domain = process.env.REACT_APP_DOMAIN || window.location.hostname;
        clearV2Token(domain);
        SecureCookieManager.clearAuthCookies();
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname]);

  if (isLoading) {
    return <div>Loading...</div>; // Same simple loading as landing and v2-frontend
  }

  // Allow access to login, error, and subscription pages (same as landing and v2-frontend)
  if (location.pathname === '/login' || location.pathname.startsWith('/error') || 
      location.pathname === '/subscription' || location.pathname === '/renew-subscription' ||
      location.pathname === '/register') {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Demo user: skip subscription check so they can see demo pages
  const token = getV2Token();
  if (token && isDemoUser(token)) {
    return <>{children}</>;
  }

  // Check subscription status for protected routes (except subscription pages)
  const isSubscriptionPage = location.pathname === '/subscription' || location.pathname === '/renew-subscription';
  
  // Block access to all pages if no subscription, suspended, or canceled (except subscription pages)
  if (!isSubscriptionPage && !subscriptionLoading) {
    if (!subscriptionStatus || subscriptionStatus === 'error' || subscriptionStatus === 'suspended' || subscriptionStatus === 'canceled') {
      const isNoSubscriptionOrError = !subscriptionStatus || subscriptionStatus === 'error';
      const isSuspended = subscriptionStatus === 'suspended';

      const headingText: string = isNoSubscriptionOrError
        ? (t('header.No Subscription') || 'No Active Subscription')
        : isSuspended
        ? (t('header.Subscription Suspended') || 'Subscription Suspended')
        : (t('header.Subscription Stopped') || 'Subscription Stopped');

      const bodyText: string = isNoSubscriptionOrError
        ? (
            t("header.You don't have an active subscription. Please subscribe to continue using our services.") ||
            "You don't have an active subscription. Please subscribe to continue using our services."
          )
        : isSuspended
        ? (
            t('header.Your subscription has been suspended. Please renew to continue using our services.') ||
            'Your subscription has been suspended. Please renew to continue using our services.'
          )
        : (
            t('header.Your subscription has been stopped. Please renew to continue using our services.') ||
            'Your subscription has been stopped. Please renew to continue using our services.'
          );

      const ctaHref: string = isNoSubscriptionOrError ? '/subscription' : '/renew-subscription';

      const ctaLabel: string = isNoSubscriptionOrError
        ? (t('header.Subscribe Now') || 'Subscribe Now')
        : (t('header.Renew Subscription') || 'Renew Subscription');

      // The SubscriptionAlert component will show at the top
      // But we still need to block the page content and show a message
      const needsSubscribe = !subscriptionStatus || subscriptionStatus === 'error';
      const subscriptionHref = needsSubscribe ? '/subscription' : '/renew-subscription';
      const subscriptionButtonLabel = needsSubscribe
        ? (t("header.Subscribe Now") || "Subscribe Now")
        : (t("header.Renew Subscription") || "Renew Subscription");

      return (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '70vh', padding: '2rem' }}>
          <div className="text-center" style={{ maxWidth: '600px' }}>
            <h4 className="mb-3">
              {headingText}
            </h4>
            <p className="text-muted mb-4">
              {bodyText}
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                window.location.href = ctaHref;
              }}
              style={{ padding: '10px 30px', fontSize: '16px' }}
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute; 